// src/utils/api.js
import axios from "axios";

const api = axios.create({
  baseURL: "/api",
  timeout: 20000,
  withCredentials: true,
});

let accessToken = localStorage.getItem("accessToken") || null;

// ---- 토큰 세팅 유틸 ----
export const setAccessToken = (t) => {
  accessToken = t;
  if (t) {
    api.defaults.headers.common.Authorization = `Bearer ${t}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
  try {
    // 다른 탭 동기화용 커스텀 이벤트
    window.dispatchEvent(new Event("access-token-updated"));
  } catch (e) {}
};

if (accessToken) {
  setAccessToken(accessToken);
}

// ---- 공통 강제 로그아웃 ----
const hardLogout = () => {
  try {
    alert("세션이 만료되었습니다. 다시 로그인해 주세요.");
  } catch (e) {}

  try {
    api.post("/logout").catch(() => {});
  } catch (e) {}

  localStorage.removeItem("accessToken");
  setAccessToken(null);
  // 뒤로가기 방지 위해 replace
  window.location.replace("/login");
};

// ---- refresh 중복 방지 (여러 요청/탭 동시 처리) ----
let refreshingPromise = null;
const refreshWaiters = [];

const callRefresh = async () => {
  if (!refreshingPromise) {
    // 첫 번째 호출만 실제 /refresh 요청
    refreshingPromise = (async () => {
      const resp = await api.post("/refresh", null, {
        withCredentials: true,
        timeout: 10000,
      });

      const newAccess = resp.data?.accessToken;
      if (!newAccess) {
        throw new Error("NO_ACCESS_FROM_REFRESH");
      }

      localStorage.setItem("accessToken", newAccess);
      setAccessToken(newAccess);
      return newAccess;
    })()
      .catch((err) => {
        throw err;
      })
      .finally(() => {
        // 기다리던 애들 모두 깨우기
        refreshWaiters.splice(0).forEach((resolve) => resolve());
        refreshingPromise = null;
      });
  } else {
    // 이미 refresh 중이면 끝날 때까지 대기
    await new Promise((resolve) => refreshWaiters.push(resolve));
  }

  // setAccessToken 에서 accessToken 갱신됨
  return accessToken;
};

// ---- 응답 인터셉터 (자동 리프레시 + 이유별 처리) ----
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error;
    if (!response || !config) return Promise.reject(error);

    const status = response.status;
    const url = config.url;

    // Access 토큰 만료로 인한 401 → refresh 시도
    if (
      status === 401 &&
      url !== "/login" &&
      url !== "/refresh" &&
      !config._retry
    ) {
      config._retry = true;

      try {
        await callRefresh();

        // 원래 요청에 Authorization 헤더 보정
        config.headers = config.headers || {};
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(config);
      } catch (refreshErr) {
        const reason = refreshErr.response?.data; // "INVALID_REFRESH" / "NO_REFRESH" / "TV_MISMATCH" / "BAD_REFRESH" ...

        if (reason === "NO_REFRESH") {
          // 쿠키 자체가 없음 → 오래된 탭 / 완전 만료
          hardLogout();
        } else if (reason === "INVALID_REFRESH") {
          // 다른 탭에서 이미 refresh 회전하고, 이 탭은 예전 rt로 요청한 케이스 가능
          const stored = localStorage.getItem("accessToken");
          if (stored) {
            // 이미 다른 탭에서 accessToken 갱신된 상태면 그걸로 한 번 더 시도
            setAccessToken(stored);
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${stored}`;
            return api(config);
          }
          hardLogout();
        } else if (reason === "TV_MISMATCH") {
          // 비밀번호 변경 / 강제 로그아웃 → 무조건 세션 종료
          hardLogout();
        } else {
          // BAD_REFRESH 등 기타 예외 → 보수적으로 전체 로그아웃
          hardLogout();
        }

        return Promise.reject(refreshErr);
      }
    }

    // 그 외 에러는 그대로 넘김
    return Promise.reject(error);
  }
);

// ---- 명시적 로그아웃 호출 ----
export const logout = async () => {
  try {
    await api.post("/logout");
  } catch (e) {}
  localStorage.removeItem("accessToken");
  setAccessToken(null);
  window.location.replace("/login");
};

export default api;
